/**
 * Therapist / org-admin jobs-to-be-done (JTBD) mapped to OrgAdminDashboard `selectedMenu` keys.
 * Used for orientation copy, breadcrumbs, and future analytics — keep in sync with OrgAdminDashboard routing.
 */

export type OrgDashboardMenuId =
  | 'dashboard-home'
  | 'overview'
  | 'users'
  | 'team-activity'
  | 'all-clients'
  | 'assigned-clients'
  | 'invitations'
  | 'my-profile'
  | 'settings'
  | 'notes'
  | 'messages'
  | 'questionnaire-management'
  | 'question-bank'
  | 'strategy-bank'
  | 'program-referral';

export type ClinicianRole = 'ORG_ADMIN' | 'THERAPIST' | 'CLINICIAN' | 'INTERNS' | 'CAREGIVER' | string | null | undefined;

/** Five primary jobs therapists and org admins perform in this hub (maps to sidebar sections). */
export const THERAPIST_JTBD: ReadonlyArray<{
  id: string;
  job: string;
  menuIds: readonly OrgDashboardMenuId[];
}> = [
  {
    id: 'jtbd-clients',
    job: 'See client caseload, open a child, and run intake or session workflows',
    menuIds: ['overview', 'all-clients', 'assigned-clients'],
  },
  {
    id: 'jtbd-team',
    job: 'Invite parents, therapists, and staff; manage pending invitations',
    menuIds: ['users', 'invitations'],
  },
  {
    id: 'jtbd-profile',
    job: 'Build your professional profile — bio, photo, intro video, and credentials',
    menuIds: ['my-profile'],
  },
  {
    id: 'jtbd-org',
    job: 'Manage organization profile, plan (freelancers), and subscription',
    menuIds: ['settings'],
  },
  {
    id: 'jtbd-notes',
    job: 'Capture notes and diary context for the practice',
    menuIds: ['notes'],
  },
  {
    id: 'jtbd-content',
    job: 'Maintain questionnaires, question banks, and strategy libraries',
    menuIds: ['questionnaire-management', 'question-bank', 'strategy-bank'],
  },
  {
    id: 'jtbd-program-referral',
    job: 'Share a program referral link and see who registered',
    menuIds: ['program-referral'],
  },
];

export const SECTION_META: Record<
  OrgDashboardMenuId,
  {
    title: string;
    subtitle: string;
    group: 'hub' | 'clients' | 'people' | 'organization' | 'work' | 'tools';
  }
> = {
  'dashboard-home': {
    title: 'Dashboard',
    subtitle: 'Schedule, caseload, and admin tools in one place.',
    group: 'hub',
  },
  overview: {
    title: 'Overview & setup',
    subtitle: 'Onboarding checklist and how intake works.',
    group: 'clients',
  },
  'all-clients': {
    title: 'All clients',
    subtitle: 'Every child profile in your organization.',
    group: 'clients',
  },
  'assigned-clients': {
    title: 'My assigned clients',
    subtitle: 'Children where you are the primary therapist.',
    group: 'clients',
  },
  users: {
    title: 'Team & users',
    subtitle: 'Staff accounts and roles.',
    group: 'people',
  },
  'team-activity': {
    title: 'Team activity',
    subtitle: 'Caseload, sessions, and AI work per therapist (last 30 days).',
    group: 'people',
  },
  invitations: {
    title: 'Invitations',
    subtitle: 'Pending parent and team invites.',
    group: 'people',
  },
  'my-profile': {
    title: 'My professional profile',
    subtitle: 'Your public clinician profile — photo, bio, intro video, and credentials.',
    group: 'organization',
  },
  settings: {
    title: 'Organization & plan',
    subtitle: 'Clinic details, billing, and freelancer plan (org admins only).',
    group: 'organization',
  },
  notes: {
    title: 'Notes & diary',
    subtitle: 'Practice notes and diary tools.',
    group: 'work',
  },
  messages: {
    title: 'Messages',
    subtitle: 'Message therapists and org staff in your organization.',
    group: 'people',
  },
  'questionnaire-management': {
    title: 'Questionnaire management',
    subtitle: 'Assign and maintain questionnaires.',
    group: 'tools',
  },
  'question-bank': {
    title: 'Question bank',
    subtitle: 'Author and organize assessment items.',
    group: 'tools',
  },
  'strategy-bank': {
    title: 'Strategy bank',
    subtitle: 'Unique library strategies with the goals that use them.',
    group: 'tools',
  },
  'program-referral': {
    title: 'Program referral',
    subtitle: 'Share your link and see who registered (commission later).',
    group: 'tools',
  },
};

const GROUP_LABEL: Record<(typeof SECTION_META)[OrgDashboardMenuId]['group'], string> = {
  hub: 'Practice hub',
  clients: 'Clients & setup',
  people: 'People',
  organization: 'Organization',
  work: 'Daily work',
  tools: 'Advanced tools',
};

export function isOrgDashboardMenuId(value: string): value is OrgDashboardMenuId {
  return value in SECTION_META;
}

export function getSectionMeta(menuId: string): (typeof SECTION_META)[OrgDashboardMenuId] | null {
  if (!isOrgDashboardMenuId(menuId)) return null;
  return SECTION_META[menuId];
}

export function getBreadcrumbLabels(menuId: string): string[] {
  const meta = getSectionMeta(menuId);
  if (!meta) return ['Practice hub'];
  return ['Practice hub', GROUP_LABEL[meta.group], meta.title];
}

export function getGroupLabelForMenu(menuId: string): string | null {
  const meta = getSectionMeta(menuId);
  if (!meta) return null;
  return GROUP_LABEL[meta.group];
}

/** Sidebar groups: item ids in display order; filtered by role in the UI. */
export const NAV_GROUPS: ReadonlyArray<{
  groupKey: keyof typeof GROUP_LABEL;
  label: string;
  itemIds: readonly OrgDashboardMenuId[];
}> = [
  {
    groupKey: 'hub',
    label: GROUP_LABEL.hub,
    itemIds: ['dashboard-home'],
  },
  {
    groupKey: 'clients',
    label: GROUP_LABEL.clients,
    itemIds: ['overview', 'all-clients', 'assigned-clients'],
  },
  {
    groupKey: 'people',
    label: GROUP_LABEL.people,
    itemIds: ['messages', 'users', 'team-activity', 'invitations'],
  },
  {
    groupKey: 'work',
    label: GROUP_LABEL.work,
    itemIds: ['notes'],
  },
  {
    groupKey: 'organization',
    label: GROUP_LABEL.organization,
    itemIds: ['my-profile', 'settings'],
  },
  {
    groupKey: 'tools',
    label: GROUP_LABEL.tools,
    itemIds: ['questionnaire-management', 'question-bank', 'strategy-bank', 'program-referral'],
  },
];

export function menuItemVisibleForRole(menuId: OrgDashboardMenuId, role: ClinicianRole): boolean {
  const r = (role || '').toUpperCase();
  if (menuId === 'dashboard-home') {
    return ['ORG_ADMIN', 'THERAPIST', 'CLINICIAN', 'INTERNS'].includes(r);
  }
  if (menuId === 'messages') {
    return ['THERAPIST', 'CLINICIAN', 'INTERNS', 'DOCTOR', 'ORG_ADMIN'].includes(r);
  }
  if (menuId === 'team-activity') {
    return r === 'ORG_ADMIN';
  }
  if (menuId === 'settings') {
    return r === 'ORG_ADMIN';
  }
  if (menuId === 'my-profile') {
    return ['ORG_ADMIN', 'THERAPIST', 'CLINICIAN', 'INTERNS'].includes(r);
  }
  if (menuId === 'all-clients') {
    return ['ORG_ADMIN', 'THERAPIST', 'CLINICIAN', 'INTERNS'].includes(r);
  }
  if (menuId === 'assigned-clients') {
    return ['THERAPIST', 'ORG_ADMIN', 'CLINICIAN', 'INTERNS'].includes(r);
  }
  if (menuId === 'program-referral') {
    return ['ORG_ADMIN', 'THERAPIST', 'CLINICIAN', 'INTERNS'].includes(r);
  }
  return true;
}

/** Short labels for the sidebar (icons are chosen in OrgAdminDashboard). */
export const NAV_ITEM_LABELS: Record<OrgDashboardMenuId, string> = {
  'dashboard-home': 'Home',
  overview: 'Overview',
  users: 'Users',
  'team-activity': 'Team activity',
  'all-clients': 'All clients',
  'assigned-clients': 'My clients',
  invitations: 'Invitations',
  'my-profile': 'My profile',
  settings: 'Org & plan',
  notes: 'Notes & diary',
  messages: 'Messages',
  'questionnaire-management': 'Questionnaires',
  'question-bank': 'Question bank',
  'strategy-bank': 'Strategy bank',
  'program-referral': 'Program referral',
};
